import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import useRouter from "common/hooks/use-router";
import usePark from "common/hooks/usePark";
import { checkEmail, checkPhoneFormat } from "common/utils/fieldsValidators";
import { isPasswordCorrect } from "common/components/Password/Password";
import rolesData from "conf/roleTypesData";

// Components
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import EditHeader from "common/components/EditHeader/EditHeader";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import RadioBlockItem from "common/components/RadioBlockItem/RadioBlockItem";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import ManagerSelect from "components/select/UserSelect/ManagerSelect";
import ObserverSelect from "components/select/UserSelect/ObserverSelect";
import SectorSelect from "components/select/SectorSelect/SectorSelect";
import PermissionToggleSection from "components/UserForm/PermissionToggleSection";

// Hooks
import useUserPermissions from "common/hooks/useUserPermissions";
import NotificationsToggleSection from "components/UserForm/NotificationsToggleSection";
import LanguageSelect from "components/select/LanguageSelect/LanguageSelect";
import i18n, { FALLBACK_LANGUAGE } from "i18n";
import useAuth from "common/hooks/useAuth";
import { locale as dayjsLocale } from "dayjs";

const NOTIFICATION_SECTIONS = [
  "observation",
  "observation_request",
  "enrichment",
  "unavailability",
  "training",
  "event",
  "assignment",
  "note",
];

const PERMISSION_SECTIONS = {
  animal: ["read", "create", "update", "delete"],
  "observation-group": ["read", "create", "update", "delete"],
  species: ["read", "create", "update", "delete"],
  superfamily: ["read", "create", "update", "delete"],
  "observation-grid": ["read", "create", "update", "delete"],
  "training-grid": ["read", "create", "update", "delete"],
  sector: ["read", "create", "update", "delete"],
  enclosure: ["read", "create", "update", "delete"],
  tag: ["read", "create", "update", "delete"],
  user: ["read", "create", "update", "delete"],
  "balance-sheet": ["read", "download"],
  imports: ["read", "species", "observation-grid", "training-grid"],
  history: ["read"],
  myPark: ["read", "update"],
};

interface UserFormProps {
  onSubmit: (values: any) => any;
  editedUser?: any;
  myAccount?: any;
}

function UserForm({ onSubmit, editedUser, myAccount }: UserFormProps) {
  const { t } = useTranslation();
  const { park } = usePark();
  const { navigate } = useRouter();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [changePassword, setChangePassword] = useState(false);
  const { user: currentUser } = useAuth();
  const {
    canManageSectors,
    canManageUsers,
    canManageCurrentUser,
    canManageMyPark,
    isEditedUserAboveMe,
  } = useUserPermissions(myAccount, editedUser);

  const initialValues = useMemo(
    () => ({
      ...(editedUser?.id
        ? {
            ...editedUser,
            role: editedUser?.role?.id ?? 3,
            park: editedUser?.park?.id,
            role_name: editedUser?.role?.name,
            sectors: editedUser?.sectors?.map((s: { id: any }) => s.id),
            observators: editedUser?.observators?.map((u: { id: any }) => u.id),
            managers: editedUser?.managers?.map((u: { id: any }) => u.id),
          }
        : {
            park: park?.id,
            language: park?.language ?? "en",
            name: undefined,
            password: undefined,
            role: 6,
            email: "",
            phoneNumber: undefined,
            blocked: false,
            confirmed: true,
            administrationPermissions: {
              animal: {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              "observation-group": {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              species: {
                read: true,
                create: false,
                update: false,
                delete: false,
                import: false,
              },
              superfamily: {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              "observation-grid": {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              "training-grid": {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              sector: {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              enclosure: {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              tag: {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              user: {
                read: true,
                create: false,
                update: false,
                delete: false,
              },
              history: {
                read: true,
              },
              "q-and-a": {
                read: true,
                update: false,
              },
              myPark: {
                read: false,
                update: false,
              },
              "balance-sheet": {
                download: false,
                read: true,
              },
              imports: {
                read: false,
                species: false,
                "observation-grid": false,
                "training-grid": false,
              },
            },
            notificationSettings: {
              observation: {
                push: true,
              },
              observation_request: {
                push: true,
              },
              enrichment: {
                push: true,
              },
              unavailability: {
                push: true,
              },
              training: {
                push: true,
              },
              event: {
                push: true,
              },
              assignment: {
                push: true,
              },
              note: {
                push: true,
              },
            },
          }),
    }),
    [editedUser, park]
  );

  const checkPassword = (value: string) =>
    errorMessage || isPasswordCorrect(value)
      ? undefined
      : t("common.passwordTooWeak");

  const validateForm = (values: any) => {
    const errors = {};

    if (values.password !== values.confirmPassword) {
      return { confirmPassword: t("common.resetPasswordError") };
    }

    return errors;
  };

  const onSubmitWrapper = async (values: any) => {
    const userData = {
      ...values,
      name: values?.username ?? "",
    };

    // Clean up hierarchy data based on role
    const roleData = rolesData.find((rd) => rd.id === userData.role);
    if (roleData?.titleKey?.includes("Admin")) {
      delete userData.managers;
      delete userData.observators;
    } else if (roleData?.titleKey?.includes("Manager")) {
      delete userData.managers;
    } else if (roleData?.titleKey?.includes("Observer")) {
      delete userData.observators;
    }

    // Ici, dans administrationPermissions, on doit supprimer tous les "all" pour chaque section
    if (roleData?.titleKey?.includes("Manager")) {
      const permissions = Object.keys(userData.administrationPermissions);
      permissions.forEach((permission) => {
        delete userData.administrationPermissions[permission].all;
      });
    }

    if (
      userData.notificationSettings?.all !== null &&
      userData.notificationSettings?.all !== undefined
    ) {
      delete userData.notificationSettings?.all;
    }
    if (userData.id === currentUser?.id) {
      i18n.changeLanguage(userData.language);
      dayjsLocale(userData.language || FALLBACK_LANGUAGE);
    }

    const res = await onSubmit(userData);

    if (res.message) {
      setErrorMessage(res.message);
    } else {
      setChangePassword(false);
    }
  };

  const displayRoleChoice =
    !myAccount && canManageUsers && !isEditedUserAboveMe;
  const displayNotificationSettings =
    Boolean(myAccount) ||
    (canManageCurrentUser && canManageUsers && !isEditedUserAboveMe);

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{ ...arrayMutators }}
      validate={validateForm}
      initialValues={initialValues}
      render={({ handleSubmit, values, form }) => (
        <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
          <EditHeader
            title={
              myAccount
                ? t("titlePages.myAccount")
                : t(`common.${editedUser?.id ? "edit" : "add"}`)
            }
            subtitle={values.name}
            icon="UsersIcon"
            errorMessage={
              errorMessage === "Username already taken"
                ? t("common.usernameAlreadyTaken")
                : errorMessage
            }
            HeaderButtonsEdit={
              <HeaderButtonsEdit
                inHeader
                onSubmit={canManageCurrentUser ? handleSubmit : undefined}
                onCancel={myAccount ? null : () => navigate("/users")}
              />
            }
          />

          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FormScrollError />

            <div className="responsive-flex gap-2 mt-2">
              {/* Main Form Section */}
              <div className="md:w-3/5 flex flex-col gap-2">
                <div className="white-box">
                  {/* Role Selection */}
                  {displayRoleChoice && (
                    <div className="mb-4">
                      <SectionTitle
                        title={t("forms.role")}
                        icon="CalendarDaysIcon"
                        className="mb-2 text-lg normal-case"
                      />
                      <FormControl
                        type="custom"
                        name="role"
                        editable={canManageCurrentUser}
                      >
                        <RadioBlock
                          Component={RadioBlockItem}
                          items={rolesData}
                          horizontal
                        />
                      </FormControl>
                    </div>
                  )}

                  {/* User Information */}
                  <div className="flex flex-col gap-4">
                    <FormControl
                      name="username"
                      title={
                        values?.role === 6
                          ? t("forms.usernameForObserver")
                          : t("forms.username")
                      }
                      editable={canManageCurrentUser}
                      required
                    />

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <FormControl
                        name="firstname"
                        title={t("common.firstname")}
                        editable={canManageCurrentUser}
                      />
                      <FormControl
                        name="lastname"
                        title={t("common.lastname")}
                        editable={canManageCurrentUser}
                      />
                    </div>
                    <div>
                      <FormControl
                        name="language"
                        title={t("forms.language")}
                        type="custom"
                        editable={canManageCurrentUser}
                        required
                      >
                        <LanguageSelect />
                      </FormControl>
                    </div>
                    {canManageMyPark && (
                      <FormControl
                        type="custom"
                        name="confirmed"
                        editable={canManageCurrentUser}
                      >
                        <ToggleSwitch
                          isDisabled={!canManageCurrentUser}
                          rightLabel={
                            values.confirmed
                              ? t("forms.confirmed")
                              : t("forms.unconfirmed")
                          }
                        />
                      </FormControl>
                    )}

                    {/* Password Section */}
                    {editedUser?.id ? (
                      <ToggleSwitch
                        rightLabel={t("forms.newPassword")}
                        value={changePassword}
                        onChange={setChangePassword}
                        isDisabled={!canManageCurrentUser}
                      />
                    ) : null}

                    {(changePassword || !editedUser?.id) && (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <FormControl
                          type="password"
                          name="password"
                          title={t("forms.password")}
                          helpText={t("common.passwordInstruction")}
                          required
                          validate={checkPassword}
                          editable={canManageCurrentUser}
                        />
                        <FormControl
                          name="confirmPassword"
                          title={t("forms.confirmPassword")}
                          type="password"
                          editable={canManageCurrentUser}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-row w-full gap-2">
                  <div className="white-box gap-2 flex-col flex flex-1">
                    <SectionTitle
                      title={t("forms.park")}
                      className="mb-2 text-lg normal-case"
                    />
                    <FormControl
                      name="sectors"
                      title={t("common.sectors")}
                      type="custom"
                      editable={canManageSectors && canManageCurrentUser}
                    >
                      <SectorSelect multiple />
                    </FormControl>
                  </div>
                </div>

                {/* Permissions Sections */}
                {!myAccount &&
                  values.role &&
                  (rolesData
                    .find((rd) => rd.id === values.role)
                    ?.titleKey?.includes("Manager") ||
                    rolesData
                      .find((rd) => rd.id === values.role)
                      ?.titleKey?.includes("Observer")) && (
                    <div className="white-box">
                      <SectionTitle
                        title={t(
                          "administrationPermissions.administrationPermissions"
                        )}
                        className="mb-2 text-lg normal-case"
                      />
                      <div className="grid-1or2 xl:gap-6">
                        {Object.entries(PERMISSION_SECTIONS).map(
                          ([section, permissions]) => (
                            <PermissionToggleSection
                              key={section}
                              title={section}
                              permissions={permissions}
                              basePath={`administrationPermissions.${section}`}
                              form={form}
                              editable={canManageCurrentUser}
                            />
                          )
                        )}
                      </div>
                    </div>
                  )}
              </div>

              {/* Sidebar */}
              <div className="w-full md:w-2/5 gap-2 flex flex-col">
                {/* Contact Information */}
                <div className="white-box">
                  <SectionTitle
                    title={t("forms.coordinates")}
                    className="mb-2 text-lg normal-case"
                  />
                  <FormControl
                    name="courriel"
                    title={t("forms.email")}
                    validate={(val) => checkEmail(val, t)}
                    editable={canManageCurrentUser}
                  />
                  <FormControl
                    name="phoneNumber"
                    title={t("forms.phoneNumber")}
                    validate={(val) =>
                      val ? checkPhoneFormat(val, t) : undefined
                    }
                    editable={canManageCurrentUser}
                  />
                  <FormControl
                    name="job"
                    title={t("common.job")}
                    editable={canManageCurrentUser}
                  />
                </div>

                {/* Hierarchy Section */}
                {values.role && (
                  <div className="white-box">
                    <SectionTitle
                      title={t("forms.hierarchy")}
                      className="mb-2 text-lg normal-case"
                    />
                    {rolesData
                      .find((rd) => rd.id === values.role)
                      ?.titleKey?.includes("Observer") && (
                      <FormControl
                        name="managers"
                        title={t("common.managers")}
                        type="custom"
                        editable={canManageCurrentUser && canManageUsers}
                      >
                        <ManagerSelect multiple />
                      </FormControl>
                    )}
                    {(rolesData
                      .find((rd) => rd.id === values.role)
                      ?.titleKey?.includes("Manager") ||
                      rolesData
                        .find((rd) => rd.id === values.role)
                        ?.titleKey?.includes("Admin")) && (
                      <FormControl
                        name="observators"
                        title={t("common.observers")}
                        type="custom"
                        editable={canManageUsers && canManageCurrentUser}
                      >
                        <ObserverSelect multiple />
                      </FormControl>
                    )}
                  </div>
                )}

                {/* Notifications Section */}
                {displayNotificationSettings && (
                  <div className="white-box">
                    <NotificationsToggleSection
                      notifications={NOTIFICATION_SECTIONS}
                      form={form}
                      editable={canManageCurrentUser}
                    />
                  </div>
                )}
              </div>
            </div>

            <HeaderButtonsEdit
              onSubmit={handleSubmit}
              onCancel={myAccount ? null : () => navigate("/users")}
            />
          </form>
        </div>
      )}
    />
  );
}

export default UserForm;
